import { FC, useCallback, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { ButtonBase, Collapse, Stack, styled } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { Dot } from 'src/components/dot';
import { icons } from 'src/theme/icons';
import { NavigationItem as Item, NavigationItemActive } from '../config';

interface NavigationProps {
  active?: boolean;
  disabled?: boolean;
  depth?: number;
}

const ignoreNavigationProps = (prop: string) =>
  !['active', 'disabled', 'depth'].includes(prop);

const NavigationButton = styled(ButtonBase, {
  shouldForwardProp: ignoreNavigationProps
})<NavigationProps>(({ theme, active, depth = 0 }) => ({
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  justifyContent: 'flex-start',
  padding: '6px',
  paddingLeft: `${16 + depth * 16}px`,
  paddingRight: '16px',
  textAlign: 'left',
  width: '100%',
  ...(active && {
    ...(depth === 0 && {
      backgroundColor: 'var(--nav-item-active-bg)'
    })
  }),
  '&:hover': {
    backgroundColor: 'var(--nav-item-hover-bg)'
  }
}));

const NavigationIcon = styled('span', {
  shouldForwardProp: ignoreNavigationProps
})<NavigationProps>(({ theme, active }) => ({
  alignItems: 'center',
  color: 'var(--nav-item-icon-color)',
  display: 'inline-flex',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  ...(active && {
    color: 'var(--nav-item-icon-active-color)'
  })
}));

const NavigationTitle = styled('span', {
  shouldForwardProp: ignoreNavigationProps
})<NavigationProps>(({ theme, active, disabled, depth = 0 }) => ({
  color: 'var(--nav-item-color)',
  flexGrow: 1,
  fontFamily: theme.typography.fontFamily,
  fontSize: depth > 0 ? 13 : 14,
  fontWeight: depth > 0 ? 500 : 600,
  lineHeight: '24px',
  whiteSpace: 'nowrap',
  ...(active && {
    color: 'var(--nav-item-active-color)'
  }),
  ...(disabled && {
    color: 'var(--nav-item-disabled-color)'
  })
}));

interface NavigationItemProps {
  item: Item;
  depth?: number;
}

export const NavigationItem: FC<NavigationItemProps> = (props) => {
  const { item, depth = 0 } = props;

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    if (item.items) setOpen((current) => !current);
    else if (item.path) navigate(item.path);
  }, [navigate, item]);

  const active = useMemo(() => {
    const match = (pattern: NavigationItemActive): boolean => {
      if (typeof pattern === 'string') return pattern === pathname;
      if (typeof pattern === 'function') return pattern(pathname);
      if (Array.isArray(pattern)) return pattern.some(match);
      return pattern.test(pathname);
    };

    return !!item.active && match(item.active);
  }, [item, pathname]);

  return (
    <li>
      <NavigationButton
        disabled={item.disabled}
        active={active}
        depth={depth}
        onClick={handleClick}
      >
        {item.icon && depth === 0 && (
          <NavigationIcon active={active}>
            <Icon icon={item.icon} />
          </NavigationIcon>
        )}
        {depth > 0 && (
          <Dot
            sx={{ marginRight: 2 }}
            color={
              active
                ? 'var(--nav-item-icon-active-color)'
                : 'var(--nav-item-icon-color)'
            }
          />
        )}
        <NavigationTitle
          active={active}
          disabled={item.disabled}
          depth={depth}
        >
          {item.title}
        </NavigationTitle>
        {!!item.items && (
          <Icon
            icon={open ? icons.chevronDown : icons.chevronUp}
            style={{ color: 'var(--nav-item-chevron-color)' }}
          />
        )}
      </NavigationButton>
      {!!item.items && (
        <Collapse
          in={open}
          sx={{ mt: 0.5 }}
        >
          <Stack
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0
            }}
          >
            {item.items.map((item, i) => (
              <NavigationItem
                item={item}
                depth={depth + 1}
                key={i}
              />
            ))}
          </Stack>
        </Collapse>
      )}
      {item.badge && <Dot color="error.main" />}
    </li>
  );
};
